<template>
  <div class="forecast-widget">
    <div class="forecast-header d-flex justify-content-between align-items-center mb-3">
      <div>
        <h5 class="mb-0 text-light">5-Day Forecast</h5>
        <small class="text-muted" v-if="forecastData && forecastData.modelRun">
          Model run: {{ formatModelRunTime(forecastData.modelRun.modelRunDate) }}
        </small>
      </div>
      <small class="text-muted">Met Office</small>
    </div>
    
    <div v-if="forecastData && forecastData.forecasts" class="forecast-days">
      <div 
        v-for="(forecast, index) in limitedForecasts" 
        :key="forecast.date"
        class="forecast-day d-flex align-items-center justify-content-between py-2"
        :class="{ 'border-bottom': index < limitedForecasts.length - 1 }"
      >
        <div class="day-info">
          <div class="fw-bold text-light">{{ formatDate(forecast.date) }}</div>
          <div class="small text-muted">{{ forecast.day.condition }}</div>
        </div>
        
        <div class="weather-icon text-center">
          <i :class="getWeatherIcon(forecast.day.weatherCode)" class="weather-icon-size"></i>
        </div>
        
        <div class="temps text-end">
          <div class="high-temp fw-bold text-light">{{ Math.round(forecast.day.temperature.max) }}°</div>
          <div class="low-temp text-muted small">{{ Math.round(forecast.night.temperature.min) }}°</div>
        </div>
        
        <div class="rain-chance text-center">
          <i class="bi bi-droplet-fill text-info small"></i>
          <div class="small text-light">{{ forecast.day.precipitation.probability || 0 }}%</div>
        </div>
      </div>
    </div>
    
    <div v-else class="text-center text-muted py-3">
      Loading forecast...
    </div>
    
    <div class="mt-2">
      <small class="text-muted">
        <i class="bi bi-info-circle me-1"></i>
        Forecast data provided by the Met Office
      </small>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DailyForecastWidget',
  props: {
    forecastData: {
      type: Object,
      default: null
    }
  },
  computed: {
    limitedForecasts() {
      if (!this.forecastData || !this.forecastData.forecasts) return [];
      // Show up to 5 days, starting from today or tomorrow
      return this.forecastData.forecasts
        .filter(forecast => forecast.day.temperature.max !== null) // Filter out incomplete data
        .slice(0, 5);
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString);
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      if (date.toDateString() === today.toDateString()) {
        return 'Today';
      } else if (date.toDateString() === tomorrow.toDateString()) {
        return 'Tomorrow';
      } else {
        return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
      }
    },
    
    formatModelRunTime(utcDateString) {
      const utcDate = new Date(utcDateString);
      // Convert to UK time (handles both GMT and BST automatically)
      return utcDate.toLocaleString('en-GB', {
        timeZone: 'Europe/London',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    getWeatherIcon(weatherCode) {
      // Weather code mapping based on common weather icon patterns
      const iconMap = {
        1: 'bi bi-sun-fill text-warning', // Sunny
        2: 'bi bi-cloud-sun-fill text-warning', // Partly cloudy
        3: 'bi bi-cloud-sun text-secondary', // Partly cloudy
        7: 'bi bi-cloudy-fill text-secondary', // Cloudy
        10: 'bi bi-cloud-drizzle-fill text-primary', // Light rain
        12: 'bi bi-cloud-rain-fill text-primary', // Heavy rain
        15: 'bi bi-cloud-snow-fill text-info', // Snow
        null: 'bi bi-question-circle text-muted' // Unknown
      };
      
      return iconMap[weatherCode] || 'bi bi-cloud text-secondary';
    }
  }
}
</script>

<style scoped>
.forecast-widget {
  background: #343a40;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #495057;
}

.forecast-day {
  min-height: 50px;
  border-color: #495057 !important;
}

.day-info {
  flex: 1;
  min-width: 80px;
}

.weather-icon {
  flex: 0 0 40px;
}

.weather-icon-size {
  font-size: 1.2rem;
}

.temps {
  flex: 0 0 40px;
}

.rain-chance {
  flex: 0 0 35px;
}

.high-temp {
  font-size: 1rem;
}

.low-temp {
  font-size: 0.8rem;
}

.forecast-header h5 {
  color: #f8f9fa;
}
</style>